// components/AI/ToolTrace.js
// Lencana "tool apa yang benar-benar dipanggil" untuk jawaban mode Coding.
//
// Dijadikan komponen BERSAMA sejak awal — bukan disalin ke dua tempat — karena
// transkrip hidup (AIChat.js) dan halaman riwayat (AIChatHistory.js) membaca
// bentuk data yang sama. Alasan yang persis sama sudah membuat ImageLightbox
// dan sessionResume.js dipisah; menyalinnya berarti setiap perubahan bentuk
// menuntut dua suntingan serempak, dan yang terlupa selalu yang jarang dibuka.
//
// KENAPA INI ADA. Model bisa menulis "saya sudah cek knowledge base" tanpa
// pernah memanggil satu tool pun — persis kelas kegagalan yang membuat jawaban
// terlihat meyakinkan sambil tidak menyentuh data sama sekali. Lencana ini
// mengubah klaim itu jadi sesuatu yang bisa diperiksa: kalau tidak ada lencana,
// tidak ada tool yang dipanggil, titik.
//
// Bentuk data berbeda di dua sumber, dan keduanya ditangani di sini:
//   - langsung dari AI Service : { name, input, ok }   (ok boolean)
//   - dari riwayat (slimTools) : { name, args,  ok }   (ok 1/0, args string)

import React from 'react';

// Nama tool → label pendek yang menyatakan APA yang terjadi, bukan nama teknis
// fungsinya. Pembaca riwayat tidak perlu tahu nama internal tool untuk menilai
// apakah jawabannya berdasar.
const TOOL_LABEL = {
  search_kb: 'knowledge base',
  read_file: 'baca berkas',
  grep_code: 'cari di kode',
  pm2_logs:  'baca log',
  write_file: 'tulis draft',
};

export default function ToolTrace({ tools, compact = false, expected = false }) {
  const none = !Array.isArray(tools) || !tools.length;

  // Jawaban mode Coding TANPA satu pun tool. Ini bukan detail teknis — ini
  // kelas jawaban yang paling sering karangan, karena model menyusunnya dari
  // ingatan tentang proyek yang tidak pernah ia baca. Terbukti berulang:
  // pertanyaan yang sama dijawab dua model berbeda tanpa tool, dan KEDUANYA
  // mengarang nama tabel, endpoint, dan fungsi lengkap dengan blok kode.
  //
  // Ketiadaan lencana sebenarnya sudah menjadi sinyal, tapi sinyal berupa
  // KETIADAAN mudah terlewat. Ini menjadikannya pernyataan.
  if (none) {
    if (!expected) return null;
    return (
      <div className={`flex flex-wrap items-center gap-1 ${compact ? 'mt-1' : 'mt-2'}`}>
        <span
          title="Model menjawab langsung tanpa membaca berkas, mencari di kode, atau menanyakan knowledge base. Periksa ulang nama berkas, tabel, dan fungsi yang disebut."
          className="inline-flex items-center gap-1 text-[10px] leading-none px-1.5 py-1 rounded-md border
                     bg-amber-50 border-amber-200 text-amber-700">
          <span aria-hidden="true">⚠</span>
          dijawab tanpa memeriksa kode
        </span>
      </div>
    );
  }

  return (
    <div className={`flex flex-wrap items-center gap-1 ${compact ? 'mt-1' : 'mt-2'}`}>
      {tools.map((tl, i) => {
        // `ok` datang sebagai boolean (langsung) atau 1/0 (dari DB). Hanya nilai
        // yang JELAS gagal yang ditandai merah — undefined dianggap berhasil,
        // supaya baris riwayat lama tidak tampak seolah semuanya error.
        const failed = tl.ok === false || tl.ok === 0;
        const label  = TOOL_LABEL[tl.name] || tl.name;
        const args   = typeof tl.args === 'string'
          ? tl.args
          : (() => { try { return JSON.stringify(tl.input ?? {}); } catch (_) { return ''; } })();
        return (
          <span key={i}
            title={`${tl.name}${args ? ` · ${args}` : ''}${failed ? ' — GAGAL' : ''}`}
            className={`inline-flex items-center gap-1 text-[10px] leading-none px-1.5 py-1 rounded-md border
              ${failed
                ? 'bg-red-50 border-red-200 text-red-700'
                : 'bg-slate-50 border-slate-200 text-slate-500'}`}>
            <span aria-hidden="true">{failed ? '✕' : '🔧'}</span>
            {label}
          </span>
        );
      })}
    </div>
  );
}
